<template>
  <v-row>
    <v-col cols="12">
      <v-row justify="space-between" align="center">
        <v-col cols="6">
          <h1>Checkbox аккаунты</h1>
        </v-col>
        <v-col cols="3" class="search-col">
          <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Поиск"
              single-line
              hide-details
              clearable
          />
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12">
      <v-row>
        <v-spacer />
        <v-col cols="auto" class="text-right">
          <v-btn color="primary" @click="navigateToCreate">
            <v-icon left>mdi-plus</v-icon>
            Создать
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          :loading="loading"
          :items-per-page="-1"
          :footer-props="footerProps"
          no-data-text="Пусто"
          no-results-text="По заданному запросу ничего не найдено"
          loading-text="Загрузка... Подождите пожалуйста"
      >
        <!-- Login column — показываем частично для безопасности -->
        <template #item.login="{ item }">
          <code>{{ item.login }}</code>
        </template>

        <!-- Password column — скрыт -->
        <template #item.password="{ item }">
          <span class="grey--text">••••••••</span>
        </template>

        <!-- License key — показываем только начало -->
        <template #item.license_key="{ item }">
          <code>{{ item.license_key.substring(0, 8) }}...</code>
        </template>

        <!-- Branches count -->
        <template #item.branches_count="{ item }">
          <v-chip small>
            {{ item.branches_count || 0 }} филиалов
          </v-chip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="text-right">
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="navigateToEdit(item.id)">
                  <v-list-item-icon>
                    <v-icon>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Редактировать</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openDeleteDialog(item)">
                  <v-list-item-icon>
                    <v-icon color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Удалить</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-col>

    <v-overlay :value="overlay" :z-index="4" />

    <ConfirmDialog
        v-model="deleteDialog.show"
        :title="deleteDialog.title"
        :message="deleteDialog.message"
        @confirm="handleDelete"
        @cancel="closeDeleteDialog"
    />

    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top right
    >
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Закрыть</v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import CheckboxLoginService from '@/api/CheckboxLoginService'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

export default {
  name: 'CheckboxLoginIndex',

  components: { ConfirmDialog },

  data() {
    return {
      search: '',
      loading: true,
      overlay: false,

      deleteDialog: {
        show: false,
        title: 'Подтвердить удаление',
        message: 'Вы уверены, что хотите удалить этот Checkbox аккаунт? Привязанные филиалы потеряют доступ к кассе.',
        itemToDelete: null
      },

      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },

      footerProps: {
        itemsPerPageText: 'Элементов на страницу',
        pageText: '{0}-{1} из {2}',
        itemsPerPageAllText: 'Все',
        itemsPerPageOptions: [-1]
      },

      headers: [
        { text: 'ID',          value: 'id',             sortable: true,  width: '60px' },
        { text: 'Название',    value: 'name',            sortable: true  },
        { text: 'Логин',       value: 'login',           sortable: true  },
        { text: 'Пароль',      value: 'password',        sortable: false },
        { text: 'License Key', value: 'license_key',     sortable: false },
        { text: 'Филиалов',    value: 'branches_count',  sortable: true  },
        { text: 'Действия',    value: 'actions',         sortable: false, width: '100px' }
      ],

      items: []
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    navigateToCreate() {
      this.$router.push({ name: 'checkbox-login-create' })
    },

    navigateToEdit(id) {
      this.$router.push({ name: 'checkbox-login-edit', params: { id } })
    },

    openDeleteDialog(item) {
      this.deleteDialog.itemToDelete = item
      this.deleteDialog.show = true
    },

    closeDeleteDialog() {
      this.deleteDialog.show = false
      this.deleteDialog.itemToDelete = null
    },

    async fetchData() {
      this.loading = true
      try {
        const response = await CheckboxLoginService.list()
        this.items = response.data
      } catch (error) {
        this.showSnackbar('Ошибка при загрузке данных', 'error')
      } finally {
        this.loading = false
      }
    },

    async handleDelete() {
      if (!this.deleteDialog.itemToDelete) return
      try {
        await CheckboxLoginService.remove(this.deleteDialog.itemToDelete.id)
        this.showSnackbar('Аккаунт успешно удален')
        await this.fetchData()
      } catch (error) {
        this.showSnackbar('Ошибка при удалении', 'error')
      } finally {
        this.closeDeleteDialog()
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    }
  }
}
</script>